import { DocumentNode, gql } from '@apollo/client';
import { Box, Button, Collapse, Divider, HStack, Link, Popover, PopoverBody, PopoverContent, PopoverTrigger, Portal, Stack, Text, VStack } from '@chakra-ui/react';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { Dinero, dinero, minimum, subtract, toSnapshot } from 'dinero.js';
import * as React from 'react';

import { CurrencyDisplay } from '@/components/Currency';
import {
  AutoPayImportantDateType,
  PaymentImportantDateType,
  SimpleImportantDateType,
} from '@/graphql-types/globalTypes';
import { useDefaultCurrency } from '@/hooks/useDefaultCurrency';
import { InfoIcon } from '@/imports/ui/chakra/feather';
import { isGreaterThanZero } from '@/utilities';

import {
  ImportantDateItem_MembershipTermImportantDateFragment,
  ImportantDateItem_MembershipTermImportantDateFragment_AutoPayMembershipTermImportantDate,
  ImportantDateItem_MembershipTermImportantDateFragment_PaymentMembershipTermImportantDate,
  ImportantDateItem_MembershipTermImportantDateFragment_SimpleMembershipTermImportantDate,
} from './graphql-types/ImportantDateItem_MembershipTermImportantDateFragment';
import { useState } from 'react';

dayjs.extend(isSameOrBefore);

export const ImportantDateItem: React.FC<{
  importantDate: ImportantDateItem_MembershipTermImportantDateFragment;
  creditBalance: Dinero<number>;
  isMembershipEndless: boolean;
  hasRelevantFailedPayment: boolean;
  newView?:
  boolean;
  tag?: string | null;
}> & { importantDateFragment: DocumentNode } = ({
  importantDate,
  creditBalance,
  isMembershipEndless,
  hasRelevantFailedPayment,
  newView=false,
  tag
}) => {
  const { currency } = useDefaultCurrency();
  const totalPrice =
    importantDate.__typename === 'AutoPayMembershipTermImportantDate'
      ? dinero(importantDate.price)
      : importantDate.__typename === 'PaymentMembershipTermImportantDate'
      ? dinero(importantDate.annualPrice)
      : null;

  const monthlyPrice =
    importantDate.__typename === 'PaymentMembershipTermImportantDate' &&
    importantDate.monthlyPrice
      ? dinero(importantDate.monthlyPrice)
      : null;

  const dineroCreditToUse =
    creditBalance && totalPrice
      ? (() => {
          // Ensure both Dinero objects have the same currency
          const totalPriceSnapshot = toSnapshot(totalPrice);
          const creditBalanceSnapshot = toSnapshot(creditBalance);
          
          // Convert creditBalance to match totalPrice's currency if they differ
          const creditBalanceWithSameCurrency =
            totalPriceSnapshot.currency.code === creditBalanceSnapshot.currency.code &&
            totalPriceSnapshot.currency.base === creditBalanceSnapshot.currency.base &&
            totalPriceSnapshot.currency.exponent === creditBalanceSnapshot.currency.exponent
              ? creditBalance
              : dinero({
                  amount: creditBalanceSnapshot.amount,
                  currency: totalPriceSnapshot.currency,
                  scale: creditBalanceSnapshot.scale,
                });
          
          return minimum([totalPrice, creditBalanceWithSameCurrency]);
        })()
      : dinero({ amount: 0, currency });

  const paymentReceived =
    (importantDate.__typename === 'PaymentMembershipTermImportantDate' &&
      importantDate.order?.paymentReceived) ||
    (importantDate.__typename === 'AutoPayMembershipTermImportantDate' &&
      importantDate.order?.paymentReceived);

  const dineroPaymentReceived = paymentReceived
    ? dinero(paymentReceived)
    : dinero({ amount: 0, currency });

  const dineroRemainingDue =
    totalPrice &&
    (isGreaterThanZero(dineroCreditToUse) ||
      isGreaterThanZero(dineroPaymentReceived))
      ? (() => {
          // Ensure all Dinero objects have the same currency as totalPrice
          const totalPriceSnapshot = toSnapshot(totalPrice);
          const paymentReceivedSnapshot = toSnapshot(dineroPaymentReceived);
          
          // Convert dineroPaymentReceived to match totalPrice's currency if they differ
          const dineroPaymentReceivedWithSameCurrency =
            totalPriceSnapshot.currency.code === paymentReceivedSnapshot.currency.code &&
            totalPriceSnapshot.currency.base === paymentReceivedSnapshot.currency.base &&
            totalPriceSnapshot.currency.exponent === paymentReceivedSnapshot.currency.exponent
              ? dineroPaymentReceived
              : dinero({
                  amount: paymentReceivedSnapshot.amount,
                  currency: totalPriceSnapshot.currency,
                  scale: paymentReceivedSnapshot.scale,
                });
          
          return [totalPrice, dineroCreditToUse, dineroPaymentReceivedWithSameCurrency].reduce(subtract);
        })()
      : dinero({ amount: 0, currency });

    const [showPaymentDetails, setShowPaymentDetails] = useState(false);
  return (
   <Box w="full" minW={0} gap="1" flexDirection={newView? "column" : "column"} display="flex" alignItems={newView? "center" : "flex-start"}>
        <HStack
          w="full"
          justify={newView ? 'center' : 'space-between'}
          align="center"
          spacing="0"
          flexWrap="wrap"
          minW={0}
          textAlign={newView ? 'center' : 'left'}
        > 
        <Stack align={newView? 'center':'flex-start'} spacing="0">
          <Text textStyle="compactStrong" textAlign="center">
            {dayjs(importantDate.date).format('MMM D, YYYY')}
          </Text>
          <ImportantDateType
            importantDate={importantDate}
            isMembershipEndless={isMembershipEndless}
            hasRelevantFailedPayment={hasRelevantFailedPayment}
            tag={tag}
          />
          {importantDate.__typename === 'PaymentMembershipTermImportantDate' &&
            importantDate.order &&
            importantDate.order.invoice &&
            importantDate.order.invoice.pdfUrl && (
              <Link
                href={importantDate.order.invoice.pdfUrl}
                fontSize="sm"
                fontWeight="normal"
                textDecoration="underline"
                color="gray.600"
              >
                Invoice {importantDate.order.invoice.number}
              </Link>
            )}
        </Stack>

        

        <Stack spacing="0">
          {monthlyPrice && (
            <Text textStyle="detailStrong" fontWeight="500" color="textSecondary.500" fontSize="12px"  whiteSpace="normal" wordBreak="break-word" overflowWrap="break-word">
              12 PAYMENTS OF{' '}
              <CurrencyDisplay
                fontWeight="500"
                color="textSecondary.500"
                value={monthlyPrice}
                // textStyle="detailStrong"
              />{' '}
              OR
            </Text>
          )}
          
         {!newView && (<Stack p="0" position="relative" top="-5px" w="full" align={newView? 'center':'flex-end'}>
              {!!totalPrice && (
                <CurrencyDisplay value={totalPrice} textStyle="bodyStrong" />
              )}
          </Stack>)
          }
        </Stack>
        
      </HStack>

      {newView && (<Stack p="0" position="relative" top="-5px" w="full" align={newView? 'center':'flex-end'}>
        {!!totalPrice && (
          <CurrencyDisplay value={totalPrice} textStyle="bodyStrong" />
        )}
        </Stack>)
      }

{newView &&
  !!totalPrice &&
  (isGreaterThanZero(dineroCreditToUse) ||
    isGreaterThanZero(dineroPaymentReceived)) && (
    <Popover placement="bottom-end">
      <PopoverTrigger>
        <Button
          variant="ghost"
          size="xs"
          w="auto"
          px="10px"
          justifyContent="space-between"
        >
          See Credit
        </Button>
      </PopoverTrigger>

      <Portal>
        <PopoverContent
          w="260px"
          p="0"
          borderRadius="md"
          boxShadow="lg"
          bg="white"
        >
          <PopoverBody p="4">
            <VStack w="full" spacing="2" align="stretch">
              {isGreaterThanZero(dineroCreditToUse) && (
                <>
                  <HStack w="full" justify="space-between">
                    <Text
                      fontSize="12px"
                      fontWeight="500"
                      color="textSecondary.500"
                    >
                      CREDIT
                    </Text>

                    <VStack
                      fontSize="12px"
                      fontWeight="500"
                      color="green.400"
                      spacing="0"
                      align="flex-end"
                    >
                      <Text>-</Text>
                      <CurrencyDisplay
                        newView
                        value={dineroCreditToUse}
                      />
                    </VStack>
                  </HStack>
                </>
              )}

              {isGreaterThanZero(dineroPaymentReceived) && (
                <>
                  <HStack
                    w="full"
                    justify="space-between"
                    textAlign="left"
                  >
                    <Text
                      fontSize="12px"
                      fontWeight="500"
                      color="textSecondary.500"
                    >
                      TOTAL AMOUNT PAID
                    </Text>

                    <VStack
                      fontSize="12px"
                      fontWeight="500"
                      color="green.400"
                      spacing="0"
                      align="flex-end"
                    >
                      <Text>-</Text>
                      <CurrencyDisplay
                        newView={true}
                        value={dineroPaymentReceived}
                      />
                    </VStack>
                  </HStack>

                  <Divider />
                </>
              )}

              <HStack
                w="full"
                justify="space-between"
                textAlign="left"
              >
                <Text
                  fontSize="12px"
                  fontWeight="500"
                  color="textSecondary.500"
                >
                  REMAINING AMOUNT DUE
                </Text>

                <CurrencyDisplay
                  newView={true}
                  value={dineroRemainingDue}
                  textStyle="bodyStrong"
                />
              </HStack>
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
)}
      {!newView && (<>
        {!!totalPrice &&
        (isGreaterThanZero(dineroCreditToUse) ||
          isGreaterThanZero(dineroPaymentReceived)) && (
          <VStack w="full" spacing="1" >
            <Divider />

            {isGreaterThanZero(dineroCreditToUse) && (
              <HStack w="full" justify="space-between">
                <Text fontSize="13px" textStyle="detailStrong" color="textSecondary.500">
                  CREDIT
                </Text>

                <VStack fontSize="13px" textStyle="bodyStrong" color="green.400" spacing="0">
                  <Text>-</Text>
                  <CurrencyDisplay value={dineroCreditToUse} />
                </VStack>
              </HStack>
            )}

            {isGreaterThanZero(dineroPaymentReceived) && (
              <HStack w="full" justify="space-between">
                <Text fontSize="13px" textStyle="detailStrong" color="textSecondary.500">
                  TOTAL AMOUNT PAID
                </Text>

                <VStack fontSize="13px" textStyle="bodyStrong" color="green.400" spacing="0">
                  <Text>-</Text>
                  <CurrencyDisplay value={dineroPaymentReceived} />
                </VStack>
              </HStack>
            )}

            <HStack w="full" justify="space-between">
              <Text fontSize="13px" textStyle="detailStrong" color="textSecondary.500">
                REMAINING AMOUNT DUE
              </Text>

              <CurrencyDisplay
                value={dineroRemainingDue}
                textStyle="bodyStrong"
              />
            </HStack>
          </VStack>
        )}
      </>)}
    </Box>
  );
};

const ImportantDateType: React.FC<{
  importantDate:
    | ImportantDateItem_MembershipTermImportantDateFragment_SimpleMembershipTermImportantDate
    | ImportantDateItem_MembershipTermImportantDateFragment_PaymentMembershipTermImportantDate
    | ImportantDateItem_MembershipTermImportantDateFragment_AutoPayMembershipTermImportantDate;
  isMembershipEndless: boolean;
  hasRelevantFailedPayment: boolean;
  tag?: string | null;
}> = ({ importantDate, isMembershipEndless, hasRelevantFailedPayment, tag }) => {
  const isPast = dayjs(importantDate.date).isSameOrBefore(dayjs());

  switch (importantDate.__typename) {
    case 'SimpleMembershipTermImportantDate':
      return (
        <SimpleMembershipTermImportantDateType
          importantDateType={importantDate.type}
          isPast={isPast}
          isMembershipEndless={isMembershipEndless}
          tag={tag}
        />
      );
    case 'AutoPayMembershipTermImportantDate':
      return (
        <AutoPayMembershipTermImportantDateType
          importantDateType={importantDate.autoPayType}
          numPaymentsRepresented={importantDate.numPaymentsRepresented}
          hasRelevantFailedPayment={hasRelevantFailedPayment}
        />
      );
    case 'PaymentMembershipTermImportantDate':
      return (
        <PaymentMembershipTermImportantDateType
          importantDateType={importantDate.paymentType}
          isPast={isPast}
        />
      );
    default:
      return <Text />;
  }
};

const SimpleMembershipTermImportantDateType: React.FC<{
  importantDateType: SimpleImportantDateType;
  isPast: boolean;
  isMembershipEndless: boolean;
  tag?: string | null;
}> = ({ importantDateType, isPast, isMembershipEndless, tag }) => {
  const style = { fontSize: '13px' };

  switch (importantDateType) {
    case SimpleImportantDateType.GRACE_PERIOD_END:
      return (
        <Text style={style}  whiteSpace="normal" wordBreak="break-word" overflowWrap="break-word">
          {isPast ? 'Membership lapsed' : 'Grace period ends'}
        </Text>
      );
    case SimpleImportantDateType.TERM_END:
      return (
        <Text style={style}  whiteSpace="normal" wordBreak="break-word" overflowWrap="break-word">
          {isPast ? (tag === 'terminate' ? 'Membership Cancelled' : 'Membership lapsed') : 'Membership term ends'}
        </Text>
      );
    case SimpleImportantDateType.TERM_START:
      return (
        <Text style={style} whiteSpace="normal"
        wordBreak="break-word"
        overflowWrap="break-word">
          {`Membership term ${isPast ? 'started' : 'starts'}${
            isMembershipEndless ? ' without an end date' : ''
          }`}
        </Text>
      );
    default:
      return <Text style={style}>Unknown</Text>;
  }
};

const AutoPayMembershipTermImportantDateType: React.FC<{
  importantDateType: AutoPayImportantDateType;
  numPaymentsRepresented: number | null;
  hasRelevantFailedPayment: boolean;
}> = ({
  importantDateType,
  numPaymentsRepresented,
  hasRelevantFailedPayment,
}) => {
  switch (importantDateType) {
    case AutoPayImportantDateType.MONTHLY:
      if (hasRelevantFailedPayment) {
        return (
          <HStack>
            <Text fontSize="13px" whiteSpace="normal" wordBreak="break-word" overflowWrap="break-word">
              Monthly payment auto-pay - Payment failed for{' '}
              {numPaymentsRepresented}
              {numPaymentsRepresented === 1 ? ' month' : ' months'}
            </Text>
            <InfoIcon ml="2" color="red.500" />
          </HStack>
        );
      } else {
        return <Text fontSize="13px" whiteSpace="normal" wordBreak="break-word" overflowWrap="break-word">Upcoming monthly payment auto-pay</Text>;
      }
    case AutoPayImportantDateType.RENEWAL:
    case AutoPayImportantDateType.MONTHLY_RENEWAL:
      return (
        <HStack spacing="1">
          <Text fontSize="13px" whiteSpace="normal" wordBreak="break-word" overflowWrap="break-word">Renewal auto-pay</Text>
          {hasRelevantFailedPayment && (
            <>
              <Text fontSize="13px">initiated</Text>
              <InfoIcon ml="2" color="red.500" />
            </>
          )}
        </HStack>
      );

    default:
      return <>Unknown</>;
  }
};

const PaymentMembershipTermImportantDateType: React.FC<{
  importantDateType: PaymentImportantDateType;
  isPast: boolean;
}> = ({ importantDateType, isPast }) => {
  switch (importantDateType) {
    case PaymentImportantDateType.MEMBERSHIP_FEE:
      return <Text style={{ fontSize: '13px' }} whiteSpace="normal" wordBreak="break-word" overflowWrap="break-word">
        {isPast ? 'Membership fee was due' : 'Membership fee due'}
        </Text>;
    case PaymentImportantDateType.RENEWAL_PERIOD_START:
      return <Text style={{ fontSize: '13px' }} whiteSpace="normal" wordBreak="break-word" overflowWrap="break-word">
        {isPast ? 'Renewal period started' : 'Renewal period starts'}
      </Text>;
    case PaymentImportantDateType.RENEWAL_DUE:
      return <Text style={{ fontSize: '13px' }}>Renewal due</Text>;
    case PaymentImportantDateType.INVOICE_DUE:
      return <Text style={{ fontSize: '13px' }}>Invoice due</Text>;
    default:
      return <Text style={{ fontSize: '13px' }}>Unknown</Text>;
  }
};

ImportantDateItem.importantDateFragment = gql`
  fragment ImportantDateItem_MembershipTermImportantDateFragment on MembershipTermImportantDate {
    date
    ... on SimpleMembershipTermImportantDate {
      date
      type
    }
    ... on PaymentMembershipTermImportantDate {
      date
      annualPrice
      monthlyPrice
      paymentType
      order {
        _id
        paymentReceived
        paymentDue
        creditToApply
        invoice {
          number
          pdfUrl
        }
      }
    }
    ... on AutoPayMembershipTermImportantDate {
      date
      price
      autoPayType
      numPaymentsRepresented
      order {
        _id
        paymentReceived
        paymentDue
        creditToApply
      }
    }
  }
`;
